import React, { useEffect, useMemo, useState } from 'react';
import { workflowAnalyzerService } from '../services/api';

const MissingPackagesModal = ({ isOpen, onClose, onContinue, packages = [], loading = false }) => {
  const [selectedFiles, setSelectedFiles] = useState({});
  const [uploadState, setUploadState] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [failedUploads, setFailedUploads] = useState([]);

  const missingPackages = useMemo(
    () => (Array.isArray(packages) ? packages.filter((pkg) => pkg?.status === 'not_found') : []),
    [packages]
  );

  const availableRemotePackages = useMemo(
    () => (Array.isArray(packages) ? packages.filter((pkg) => pkg?.status === 'available_remote') : []),
    [packages]
  );

  useEffect(() => {
    if (!isOpen) return;

    setSelectedFiles({});
    setUploadState({});
    setIsSubmitting(false);
    setFailedUploads([]);
  }, [isOpen, packages]);

  if (!isOpen) return null;

  const handleSelectFile = (packageId, file) => {
    setSelectedFiles((prev) => ({ ...prev, [packageId]: file || null }));
    setUploadState((prev) => ({
      ...prev,
      [packageId]: { status: 'idle', message: '' },
    }));
  };

  const handleUploadProvidedAndContinue = async () => {
    setFailedUploads([]);

    const selectedPackageIds = missingPackages
      .map((pkg) => pkg.id)
      .filter((packageId) => Boolean(selectedFiles[packageId]));

    if (selectedPackageIds.length === 0) {
      onContinue();
      return;
    }

    setIsSubmitting(true);

    try {
      const currentFailures = [];

      for (const packageId of selectedPackageIds) {
        const selectedFile = selectedFiles[packageId];
        if (!selectedFile) {
          continue;
        }

        setUploadState((prev) => ({
          ...prev,
          [packageId]: { status: 'uploading', message: 'Uploading library...' },
        }));

        try {
          const response = await workflowAnalyzerService.uploadLibrary(selectedFile);
          const success = response?.data?.success;

          if (!success) {
            throw new Error(response?.data?.error || 'Failed to upload library');
          }

          setUploadState((prev) => ({
            ...prev,
            [packageId]: { status: 'uploaded', message: 'Library uploaded successfully.' },
          }));
        } catch (error) {
          const message = error?.response?.data?.error || error.message || 'Upload failed';

          currentFailures.push({
            packageId,
            reason: message,
          });

          setUploadState((prev) => ({
            ...prev,
            [packageId]: { status: 'error', message },
          }));
        }
      }

      if (currentFailures.length > 0) {
        setFailedUploads(currentFailures);
        return;
      }

      onContinue();
    } finally {
      setIsSubmitting(false);
    }
  };

  const getBadgeStyle = (status) => {
    if (status === 'uploaded') {
      return {
        backgroundColor: 'rgba(16,185,129,0.15)',
        color: 'rgba(16,185,129,0.95)',
        border: '1px solid rgba(16,185,129,0.35)'
      };
    }
    if (status === 'uploading') {
      return {
        backgroundColor: 'rgba(245,158,11,0.15)',
        color: 'rgba(245,158,11,0.95)',
        border: '1px solid rgba(245,158,11,0.35)'
      };
    }
    if (status === 'error') {
      return {
        backgroundColor: 'rgba(239,68,68,0.15)',
        color: 'rgba(239,68,68,0.95)',
        border: '1px solid rgba(239,68,68,0.35)'
      };
    }

    return {
      backgroundColor: 'rgba(239,68,68,0.15)',
      color: 'rgba(239,68,68,0.95)',
      border: '1px solid rgba(239,68,68,0.35)'
    };
  };

  const remainingMissing = missingPackages.filter((pkg) => uploadState[pkg.id]?.status !== 'uploaded').length;
  const selectedPackageCount = missingPackages.filter((pkg) => Boolean(selectedFiles[pkg.id])).length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="w-full max-w-4xl h-[90vh] max-h-[90vh] overflow-hidden flex flex-col" style={{ backgroundColor: 'var(--tqa-surface)', border: '1px solid var(--tqa-border-dark)' }}>
        <div className="px-6 py-4 border-b shrink-0" style={{ borderColor: 'var(--tqa-border-dark)' }}>
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>Missing Dependencies</h3>
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
                Some required packages were not found on the Workflow Analyzer server. Uploading them is optional and only affects Workflow Analyzer execution.
              </p>
              <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
                If you continue without uploading a missing package, workflows that depend on it will be skipped by Workflow Analyzer. Other features such as project chat and agentic code analysis are not impacted.
              </p>
              <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>
                These dependency packages are support libraries used to analyze the main process; the dependency packages themselves are not analyzed.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-xl leading-none"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              ×
            </button>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto min-h-0 flex-1" style={{ backgroundColor: 'var(--tqa-surface-2)' }}>
          {missingPackages.length === 0 ? (
            <div className="p-4" style={{ border: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface)' }}>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>
                No missing packages were detected.
              </p>
            </div>
          ) : (
            missingPackages.map((pkg) => {
              const status = uploadState[pkg.id]?.status || 'idle';
              const statusText =
                status === 'uploaded'
                  ? 'Uploaded'
                  : status === 'uploading'
                    ? 'Uploading'
                    : status === 'error'
                      ? 'Error'
                      : 'Not Found';

              return (
                <div
                  key={pkg.id}
                  className="p-4 space-y-3"
                  style={{ border: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface)' }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'rgba(255,255,255,0.88)' }}>{pkg.id}</p>
                      <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>Required version: {pkg.requestedVersion || 'N/A'}</p>
                    </div>
                    <span className="px-2 py-1 text-xs font-medium" style={getBadgeStyle(status)}>{statusText}</span>
                  </div>

                  <div className="p-3" style={{ border: '1px dashed var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface-2)' }}>
                    <input
                      type="file"
                      accept=".nupkg"
                      onChange={(event) => handleSelectFile(pkg.id, event.target.files?.[0])}
                      className="w-full text-xs"
                      style={{ color: 'rgba(255,255,255,0.75)' }}
                    />
                    <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      Upload a matching .nupkg for this dependency.
                    </p>
                  </div>

                  {uploadState[pkg.id]?.message && status !== 'error' ? (
                    <div className="flex items-center justify-end">
                      <span
                        className="text-xs"
                        style={{
                          color:
                            status === 'error'
                              ? 'rgba(239,68,68,0.95)'
                              : status === 'uploaded'
                                ? 'rgba(16,185,129,0.95)'
                                : 'rgba(255,255,255,0.55)'
                        }}
                      >
                        {uploadState[pkg.id].message}
                      </span>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}

          {availableRemotePackages.length > 0 && (
            <div className="p-4" style={{ border: '1px solid var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface)' }}>
              <p className="text-sm font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.82)' }}>Available from remote feed</p>
              <div className="space-y-1">
                {availableRemotePackages.map((pkg) => (
                  <p key={pkg.id} className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                    {pkg.id} ({pkg.requestedVersion || 'N/A'})
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t flex items-center justify-between shrink-0" style={{ borderColor: 'var(--tqa-border-dark)', backgroundColor: 'var(--tqa-surface)' }}>
          <div className="w-full space-y-3">
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {remainingMissing > 0
                ? `${remainingMissing} missing package${remainingMissing === 1 ? '' : 's'} still not uploaded.`
                : 'All missing packages in this list have been uploaded.'}
            </p>

            {failedUploads.length > 0 && (
              <div className="p-3" style={{ border: '1px solid rgba(239,68,68,0.35)', backgroundColor: 'rgba(239,68,68,0.08)' }}>
                <p className="text-sm font-semibold" style={{ color: 'rgba(239,68,68,0.95)' }}>
                  Some package uploads failed
                </p>
                <ul className="mt-2 space-y-1">
                  {failedUploads.map((failed) => (
                    <li key={failed.packageId} className="text-xs" style={{ color: 'rgba(255,255,255,0.78)' }}>
                      {failed.packageId}: {failed.reason || 'Unknown error'}
                    </li>
                  ))}
                </ul>
                <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  Do you want to go back and try again, or proceed without these packages?
                </p>
                <div className="flex items-center justify-end space-x-2 mt-3">
                  <button
                    type="button"
                    onClick={() => setFailedUploads([])}
                    className="btn-secondary px-3 py-1.5 text-xs"
                    disabled={loading || isSubmitting}
                  >
                    Go back and try again
                  </button>
                  <button
                    type="button"
                    onClick={onContinue}
                    className="btn-primary px-3 py-1.5 text-xs"
                    disabled={loading || isSubmitting}
                  >
                    Proceed without failed packages
                  </button>
                </div>
              </div>
            )}

            {failedUploads.length === 0 && (
              <div className="flex items-center justify-end space-x-2">
                <button type="button" onClick={onClose} className="btn-secondary px-4 py-2 text-sm" disabled={loading || isSubmitting}>
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleUploadProvidedAndContinue}
                  className="btn-primary px-4 py-2 text-sm"
                  disabled={loading || isSubmitting}
                >
                  {isSubmitting
                    ? 'Uploading provided packages...'
                    : selectedPackageCount > 0
                      ? 'Upload provided packages and continue'
                      : 'Continue without packages'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissingPackagesModal;
